/**
 * Juuni Score Service — Composite operational quality score per annotator.
 *
 * Formula: (Consistency × 0.35) + (Reliability × 0.30) + (Quality × 0.20) + (Availability × 0.15)
 *
 * Each dimension is 0-100. Final score is 0-100.
 * Computed from real data: Insightful hours, batch completions, quality logs, assignments.
 */

import { supabase } from '../lib/supabase';
import { safeQuery, safeMutation } from '../utils/safeSupabase';
import { parseConfigValue } from '../utils/integrationConfig';

// Default weights (customizable per org via system_config)
const DEFAULT_WEIGHTS = { consistency: 0.35, reliability: 0.30, quality: 0.20, availability: 0.15 };

// ─── Bulk Data Fetch ──────────────────────────────────────

/**
 * Fetch all data needed to compute Juuni Scores for all annotators in an org.
 * One set of queries, not per-annotator.
 *
 * @param {string} orgId
 * @returns {Object} { assignments, batches, qualityLogs, weights }
 */
export async function fetchJuuniScoreData(orgId) {
  const [assignRes, batchRes, qualityRes, configRes] = await Promise.all([
    safeQuery(
      supabase.from('assignment')
        .select('individual_id, job_id, status, assigned_at, completed_at, hours_logged, rate')
        .eq('org_id', orgId),
      'juuniScore-fetchAssignments'
    ),
    safeQuery(
      supabase.from('annotation_batch')
        .select('individual_id, status, total_items, items_completed, started_at, submitted_at, assigned_at')
        .eq('org_id', orgId)
        .eq('is_overlap', false),
      'juuniScore-fetchBatches'
    ),
    safeQuery(
      supabase.from('quality_log')
        .select('individual_id, accuracy_percent, created_at')
        .eq('org_id', orgId)
        .not('accuracy_percent', 'is', null)
        .order('created_at', { ascending: false }),
      'juuniScore-fetchQualityLogs'
    ),
    safeQuery(
      supabase.from('system_config')
        .select('value')
        .eq('key', 'juuni_score.weights')
        .eq('org_id', orgId)
        .maybeSingle(),
      'juuniScore-fetchWeightsConfig'
    ),
  ]);

  let weights = DEFAULT_WEIGHTS;
  try {
    const parsed = parseConfigValue(configRes.data?.value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      weights = parsed;
    }
  } catch { /* intentionally empty — use defaults */ }

  return {
    assignments: assignRes.data || [],
    batches: batchRes.data || [],
    qualityLogs: qualityRes.data || [],
    weights,
  };
}

// ─── Dimension Computations ───────────────────────────────

/**
 * Consistency (35%): How regularly they show up.
 * days with activity / expected days over trailing 4 weeks.
 */
function computeConsistency(annotatorId, assignments, batches) {
  // Use batch activity as proxy for attendance
  const myBatches = batches.filter(b => b.individual_id === annotatorId);
  if (myBatches.length === 0) return null; // No data

  // Count unique dates with batch activity (started_at or submitted_at)
  const activeDates = new Set();
  for (const b of myBatches) {
    if (b.started_at) activeDates.add(new Date(b.started_at).toISOString().slice(0, 10));
    if (b.submitted_at) activeDates.add(new Date(b.submitted_at).toISOString().slice(0, 10));
  }

  // Expected: 4 days/week × 4 weeks = 16 days over trailing month
  const expectedDays = 16;
  const score = Math.min(100, (activeDates.size / expectedDays) * 100);
  return Math.round(score * 10) / 10;
}

/**
 * Reliability (30%): Do they deliver on time?
 * Batches submitted / batches assigned.
 */
function computeReliability(annotatorId, batches) {
  const myBatches = batches.filter(b => b.individual_id === annotatorId);
  if (myBatches.length === 0) return null;

  const assigned = myBatches.length;
  // In 4-state lifecycle, "submitted" (annotator completed their work) = any
  // state past In Progress — In Review, Delivered, Accepted. Rejected-and-
  // bounced-back batches are currently In Progress again, so they correctly
  // don't count toward reliability (annotator hasn't re-completed yet).
  const submitted = myBatches.filter(b =>
    ['In Review', 'Delivered', 'Accepted'].includes(b.status)
  ).length;

  const score = assigned > 0 ? (submitted / assigned) * 100 : 0;
  return Math.round(score * 10) / 10;
}

/**
 * Quality (20%): How good is their work?
 * Average of last 5 quality entries.
 */
function computeQuality(annotatorId, qualityLogs) {
  const myLogs = qualityLogs
    .filter(l => l.individual_id === annotatorId && l.accuracy_percent != null)
    .slice(0, 5); // Already sorted DESC

  if (myLogs.length === 0) return null;

  const avg = myLogs.reduce((s, l) => s + Number(l.accuracy_percent), 0) / myLogs.length;
  return Math.round(avg * 10) / 10;
}

/**
 * Availability (15%): Are they available for new work?
 * Based on current active assignment count vs capacity.
 */
function computeAvailability(annotatorId, assignments) {
  const myAssignments = assignments.filter(a => a.individual_id === annotatorId);
  if (myAssignments.length === 0) return null; // No assignment history — no score
  const activeAssignments = myAssignments.filter(a => a.status === 'Active' || a.status === 'Onboarding');

  // Assume max 3 concurrent assignments = fully booked
  const maxConcurrent = 3;
  const utilization = activeAssignments.length / maxConcurrent;
  const score = Math.max(0, Math.min(100, (1 - utilization) * 100));
  return Math.round(score * 10) / 10;
}

// ─── Score Computation ────────────────────────────────────

/**
 * Compute Juuni Score for a single annotator.
 *
 * @param {string} annotatorId
 * @param {Object} data - from fetchJuuniScoreData
 * @returns {Object} { score, dimensions: { consistency, reliability, quality, availability }, hasData }
 */
export function computeJuuniScore(annotatorId, data) {
  const { assignments, batches, qualityLogs, weights } = data;

  const dimensions = {
    consistency: computeConsistency(annotatorId, assignments, batches),
    reliability: computeReliability(annotatorId, batches),
    quality: computeQuality(annotatorId, qualityLogs),
    availability: computeAvailability(annotatorId, assignments),
  };

  // Count dimensions with data
  const withData = Object.entries(dimensions).filter(([, v]) => v !== null);
  if (withData.length === 0) return { score: null, dimensions, hasData: false };

  // Compute weighted score (redistribute weights if some dimensions have no data)
  const totalWeight = withData.reduce((s, [k]) => s + (weights[k] || 0), 0);
  const score = withData.reduce((s, [k, v]) => {
    const normalizedWeight = totalWeight > 0 ? (weights[k] || 0) / totalWeight : 0;
    return s + v * normalizedWeight;
  }, 0);

  return {
    score: Math.round(score * 10) / 10,
    dimensions,
    hasData: true,
  };
}

/**
 * Compute Juuni Scores for all annotators in bulk.
 *
 * @param {Array} annotators - [{ id, name, ... }]
 * @param {Object} data - from fetchJuuniScoreData
 * @returns {Object} { [annotatorId]: { score, dimensions, hasData } }
 */
export function computeAllJuuniScores(annotators, data) {
  const scores = {};
  for (const a of (annotators || [])) {
    scores[a.id] = computeJuuniScore(a.id, data);
  }
  return scores;
}

/**
 * Save computed Juuni Score to the individual table.
 */
export async function saveJuuniScore(annotatorId, score) {
  if (!annotatorId || score == null) return;
  await safeMutation(
    supabase.from('individual').update({
      juuni_score: score,
      juuni_score_updated_at: new Date().toISOString(),
    }).eq('id', annotatorId),
    'juuniScore-saveScore'
  );
}

export async function persistJuuniScores(annotators, computedScores, orgId = null) {
  const changedScores = (annotators || [])
    .map(annotator => ({
      annotatorId: annotator.id,
      previousScore: annotator.juuni_score,
      nextScore: computedScores?.[annotator.id]?.score,
      dimensions: computedScores?.[annotator.id]?.dimensions,
    }))
    .filter(row => row.nextScore != null)
    .filter(row => row.previousScore == null || Math.abs(Number(row.previousScore) - row.nextScore) >= 0.1);

  await Promise.all(changedScores.map(async (row) => {
    await saveJuuniScore(row.annotatorId, row.nextScore);
    // Persist to history for trend display (silently skip if RLS blocks — non-admin roles)
    await supabase.from('juuni_score_history').insert({
      user_id: row.annotatorId,
      org_id: orgId,
      score: row.nextScore,
      consistency: row.dimensions?.consistency ?? null,
      reliability: row.dimensions?.reliability ?? null,
      quality: row.dimensions?.quality ?? null,
      availability: row.dimensions?.availability ?? null,
    }).then(({ error }) => {
      if (error) console.debug('[juuniScore] History insert skipped (RLS):', error.message);
    });
  }));
}

/**
 * Fetch Juuni Score history for trend display.
 * Returns array of { score, consistency, reliability, quality, availability, computedAt }
 * ordered newest first, limited to `limit` entries.
 */
export async function fetchJuuniScoreHistory(userId, limit = 12) {
  if (!userId) return [];
  const { data, error } = await supabase.from('juuni_score_history')
    .select('score, consistency, reliability, quality, availability, computed_at')
    .eq('user_id', userId)
    .order('computed_at', { ascending: true })
    .limit(limit);
  if (error) {
    console.warn('[juuniScoreService] History fetch failed:', error.message);
    return [];
  }
  return (data || []).map(h => ({
    score: h.score,
    consistency: h.consistency,
    reliability: h.reliability,
    quality: h.quality,
    availability: h.availability,
    date: h.computed_at,
    label: h.computed_at ? new Date(h.computed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
  }));
}

/**
 * Score color based on value.
 */
export function getScoreColor(score) {
  // Theme-aware tiers — brighter forest/amber/burgundy on admin-dark,
  // darker equivalents on light theme. Matches the scoreColor helpers on
  // BossDashboard / SPLDashboard for palette consistency.
  if (score == null) return 'var(--text-muted)';
  if (score >= 80) return 'var(--text-success)';
  if (score >= 60) return 'var(--text-warning)';
  return 'var(--text-error)';
}
